import { useState } from 'react';
import { Save, Truck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';

export const PortalDeliverySetting = ({ pharmacyId, pharmacyName, initial }) => {
  const [enabled, setEnabled] = useState(!!initial?.enabled);
  const [note, setNote] = useState(initial?.note || '');
  const [radius, setRadius] = useState(initial?.radius_km ? String(initial.radius_km) : '');
  const radiusValue = radius.trim() ? Number(radius) : null;
  const radiusInvalid = radiusValue !== null && !(radiusValue >= 0.5 && radiusValue <= 100);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/portal-links/${pharmacyId}/delivery`, { enabled, note, radius_km: radiusInvalid ? null : radiusValue });
      toast.success(enabled ? 'Patients will see that you deliver' : 'Delivery hidden from Find a pharmacy');
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Could not save delivery.');
    } finally { setSaving(false); }
  };

  return (
    <div className="border-t pt-5 space-y-3" data-testid="portal-delivery-setting">
      <h3 className="font-medium flex items-center gap-2"><Truck className="w-4 h-4" />{pharmacyName} · Delivery</h3>
      <div className="flex items-center gap-3">
        <Switch id="portal-delivery-enabled" checked={enabled} onCheckedChange={setEnabled} data-testid="portal-delivery-toggle" />
        <Label htmlFor="portal-delivery-enabled">We deliver to patients</Label>
      </div>
      <p className="text-xs text-muted-foreground">Shown on Find a pharmacy for all your branches, so patients can filter for delivery.</p>
      {enabled && (
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
          <div className="space-y-2">
            <Label htmlFor="portal-delivery-radius">Delivery distance from each branch (km)</Label>
            <Input id="portal-delivery-radius" type="number" inputMode="decimal" min="0.5" max="100" step="0.5"
              placeholder="e.g. 5" value={radius} onChange={e => setRadius(e.target.value)} data-testid="portal-delivery-radius" />
            {radiusInvalid
              ? <p className="text-xs text-red-600" data-testid="portal-delivery-radius-error">Enter a distance between 0.5 and 100 km.</p>
              : <p className="text-xs text-muted-foreground">Drawn as a circle on the patient map. Leave empty for no set limit.</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="portal-delivery-note">Delivery note (optional)</Label>
            <Input id="portal-delivery-note" maxLength={120} placeholder="e.g. Same day before 4pm" value={note}
              onChange={e => setNote(e.target.value)} data-testid="portal-delivery-note" />
          </div>
        </div>
      )}
      <Button onClick={save} disabled={saving || (enabled && radiusInvalid)} data-testid="portal-delivery-save"><Save className="w-4 h-4 mr-2" />Save delivery</Button>
    </div>
  );
};
